import React, { memo, useState } from "react";
import styles from "./CisBenchmarkExclusions.module.scss";
import ButtonComponent from "../../../../../../../../../app/common-components/ButtonComponent";
import SearchInput from "../../../../../../../../../app/common-components/SearchInput/SearchInput";
import CustomModal from "../../../../../../../../../app/common-components/CustomModal";
import Spinners from "../../../../../../../../../app/common-components/Spinner";
import CompleteTableData from "../../../../../../../../../app/common-components/Table/CompleteTableData";
import PaginationControls from "../../../../../../../../../app/common-components/Table/PaginationControls";
import DragDropConfigModal from "../../../../DragDropConfigModal/DragDropConfigModal";
import SecurityCardDashboard from "./SecurityCardDashboard";
import { CIS_BENCHMARK_EXCLUSION_LABELS } from "./CisBenchmarkExclusions.constants";


const CisBenchmarkExclusions = ({
    data = [],
    totalCount = 0,
    loading = false,
    auditFilesList = [],
    auditFilesVersionMap = {},
    isActionLoading = false,
    isAuditFileListLoading = false,
    isDeleteLoading = false,
    searchKey = "",
    pageNo = 0,
    pageLimit = 10,
    onSave,
    onDelete,
    onSearch,
    onPageNoChange,
    onPageLimitChange,
    onLoadHistory,
}) => {
    const [state, setState] = useState({
        addBodyOpenAdd: false,
        addBodyOpenInfo: false,
        addBodyOpenDelete: false,
        modelRowData: null,
        historyData: [],
        historyTotalCount: 0,
        historyPageNo: 0,
        historyPageLimit: 10,
        historySearchKey: "",
        historyLoading: false,
    });
    const [localSearch, setLocalSearch] = useState(searchKey);

    const addBodyOpen = (row = null) => {
        setState(prev => ({ ...prev, addBodyOpenAdd: true, modelRowData: row }));
    };

    const addBodyClose = () => {
        setState(prev => ({ ...prev, addBodyOpenAdd: false, modelRowData: null }));
    };

    const addInfoBodyOpen = (row) => {
        if (!row) { return; }
        setState(prev => ({
            ...prev,
            addBodyOpenInfo: true,
            modelRowData: row,
            historyData: [],
            historyTotalCount: 0,
            historyPageNo: 0,
            historyPageLimit: 10,
            historySearchKey: "",
            historyLoading: false,
        }));
        fetchHistoryData(row?.id, 0, 10, { keyword: "" });
    };

    const fetchHistoryData = (configId, pageNumber, itemsPerPage, payload) => {
        setState(prev => ({ ...prev, historyLoading: true }));
        onLoadHistory?.(configId, pageNumber, itemsPerPage, payload)
            ?.then((response) => {
                setState(prev => ({
                    ...prev,
                    historyData: response?.data || [],
                    historyTotalCount: response?.totalCount || 0,
                    historyLoading: false,
                }));
            })
            ?.catch(() => {
                setState(prev => ({
                    ...prev,
                    historyData: [],
                    historyTotalCount: 0,
                    historyLoading: false,
                }));
            });
    };

    const addInfoBodyClose = () => {
        setState(prev => ({
            ...prev,
            addBodyOpenInfo: false,
            modelRowData: null,
            historyData: [],
            historyTotalCount: 0,
            historyPageNo: 0,
            historyPageLimit: 10,
            historySearchKey: "",
        }));
    };

    const deleteBodyOpen = (row) => {
        if (!row || !row.id) { return; }
        setState(prev => ({ ...prev, addBodyOpenDelete: true, modelRowData: row }));
    };

    const deleteBodyClose = () => {
        setState(prev => ({ ...prev, addBodyOpenDelete: false, modelRowData: null }));
    };

    const handleSaveAndClose = (formData) => {
        onSave?.(formData, addBodyClose);
    };

    const handleDeleteAndClose = () => {
        onDelete?.(state.modelRowData?.id, deleteBodyClose);
    };

    const historyColumns = [
        {
            Header: "Justification",
            accessor: "justification",
            tdClassName: "text-start",
            disableSortBy: true,
            Cell: ({ value }) => (
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    {value || "-"}
                </div>
            ),
        },
        {
            Header: "Updated By",
            accessor: "updatedBy",
            tdClassName: "text-center",
            disableSortBy: true,
            Cell: ({ value }) => <>{value || "-"}</>,
        },
        {
            Header: "Updated On",
            accessor: "updatedDt",
            tdClassName: "text-center",
            disableSortBy: true,
            Cell: ({ value }) => <>{value || "-"}</>,
        },
        {
            Header: "Changes",
            accessor: "message",
            tdClassName: "text-start",
            disableSortBy: true,
            Cell: ({ value }) => (
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                    {value || "-"}
                </div>
            ),
        },
    ];

    const handleHistoryPageNoChange = (historyPageNo) => {
        setState(prev => ({ ...prev, historyPageNo }));
        fetchHistoryData(state.modelRowData?.id, historyPageNo, state.historyPageLimit, {
            keyword: state.historySearchKey || "",
        });
    };

    const handleHistoryPageLimitChange = (historyPageLimit) => {
        setState(prev => ({ ...prev, historyPageLimit, historyPageNo: 0 }));
        fetchHistoryData(state.modelRowData?.id, 0, historyPageLimit, {
            keyword: state.historySearchKey || "",
        });
    };

    const onHistorySearch = (searchKey) => {
        setState(prev => ({ ...prev, historySearchKey: searchKey, historyPageNo: 0 }));
        fetchHistoryData(state.modelRowData?.id, 0, state.historyPageLimit, {
            keyword: searchKey,
        });
    };

    const addInfoBody = () => {
        return (
            <div>
                <h4>Information</h4>
                <CompleteTableData
                    isLoading={state.historyLoading}
                    data={state.historyData || []}
                    columns={historyColumns}
                    listCount={state.historyTotalCount || 0}
                    pageLimit={state.historyPageLimit || 10}
                    totalPageCount={Math.ceil((state.historyTotalCount || 0) / (state.historyPageLimit || 10)) || 1}
                    curentPageNumber={state.historyPageNo || 0}
                    btnData={[]}
                    searchKey={state.historySearchKey || ""}
                    moveSearchToEnd={true}
                    onPageNoChange={handleHistoryPageNoChange}
                    onPageLimitChange={handleHistoryPageLimitChange}
                    onSearch={onHistorySearch}
                />
            </div>
        );
    };

    const buildModalInitialData = (row) => {
        if (!row) { return null; }
        const auditFiles = (row.auditFiles || []).filter(f => f.fileName);
        return {
            ...row,
            ruleName: row.controlType || '',
            auditFileNames: auditFiles.map(f => f.fileName),
            reason: row.reasonForChange || '',
            fileRules: auditFiles.reduce((acc, f) => {
                acc[f.fileName] = f.rulesToExclude || [];
                return acc;
            }, {}),
        };
    };

    const addBody = () => (
        <DragDropConfigModal
            availableItems={auditFilesList}
            isOpen={state.addBodyOpenAdd}
            onClose={addBodyClose}
            onSave={handleSaveAndClose}
            initialData={buildModalInitialData(state.modelRowData)}
            isLoading={isActionLoading || isAuditFileListLoading}
            labels={CIS_BENCHMARK_EXCLUSION_LABELS}
            fieldMap={{
                nameReadKey: 'ruleName',
                itemsReadKey: 'auditFileNames',
                keywordsReadKey: 'keywords',
                reasonReadKey: 'reason',
                fileRulesReadKey: 'fileRules',
                buildSaveData: (formState) => ({
                    controlType: formState.name,
                    reasonForChange: formState.reason,
                    auditFiles: formState.selectedItems.map((fileName) => ({
                        fileName,
                        benchmarkVersion: auditFilesVersionMap?.[fileName] || "",
                        rulesToExclude: formState.fileRules?.[fileName] || [],
                    })),
                    id: formState.id || null,
                }),
            }}
            showReason={true}
            showLoadingSpinner={true}
            disableNameOnEdit={true}
            nameValidationRegex={/^[a-zA-Z0-9\s\-_.]+$/}
            keywordsRequireNumericStart={true}
            useDropdownMode={true}
        />
    );

    const modalDeleteBody = () => (
        <>
            <h4 className="delete-h">Delete CIS Benchmark Exclusion Rule</h4>
            {isDeleteLoading ? (
                <Spinners />
            ) : (
                <div>
                    <p>Are you sure you want to delete this exclusion rule?</p>
                    <div className="btnswrap btn-dlt">
                        <ButtonComponent
                            text="Cancel"
                            className={"cancelBtnStyle btn btn-primary"}
                            onClick={deleteBodyClose}
                        />
                        <ButtonComponent
                            text="Delete"
                            className={"btnStyle btn btn-primary"}
                            onClick={handleDeleteAndClose}
                        />
                    </div>
                </div>
            )}
        </>
    );

    return (
        <>
            <div className={styles.header}>
                <h4 className={styles.title}>CIS Benchmark Exclusions</h4>
                <div className={styles.headerActions}>
                    <ButtonComponent
                        text="Create"
                        className="solid-btn"
                        onClick={() => addBodyOpen()}
                    />
                    <SearchInput
                        searchInputValue={localSearch}
                        handleOnSearch={(e) => {
                            const value = e.target.value;
                            setLocalSearch(value);
                            if (value === '') { onSearch?.(''); }
                        }}
                        handleSearchClick={(value) => onSearch?.(value)}
                    />
                </div>
            </div>

            <div className={styles.cardContainer}>
                {loading ? (
                    <Spinners />
                ) : (
                    <SecurityCardDashboard
                        data={data}
                        onEdit={addBodyOpen}
                        onDelete={deleteBodyOpen}
                        onInfo={addInfoBodyOpen}
                    />
                )}
            </div>

            <PaginationControls
                listCount={totalCount}
                isLoading={loading}
                curentPageNumber={pageNo}
                pageLimit={pageLimit}
                onPageNoChange={onPageNoChange}
                onPageLimitChange={onPageLimitChange}
                gotoPage={() => { }}
                previousPage={() => { }}
                nextPage={() => { }}
                setPageSize={() => { }}
                onSearch={onSearch}
            />

            <CustomModal
                modalOpen={state.addBodyOpenAdd}
                modalBody={addBody}
                modalHide={addBodyClose}
                closeBtn={true}
                closeBtnFuc={addBodyClose}
                size="xl"
            />

            <CustomModal
                modalOpen={state.addBodyOpenInfo}
                modalBody={addInfoBody}
                modalHide={addInfoBodyClose}
                closeBtn={true}
                closeBtnFuc={addInfoBodyClose}
                size="lg"
            />

            <CustomModal
                modalOpen={state.addBodyOpenDelete}
                modalBody={modalDeleteBody}
                modalHide={deleteBodyClose}
                closeBtn={true}
                closeBtnFuc={deleteBodyClose}
                size="md"
            />
        </>
    );
};

export default memo(CisBenchmarkExclusions);
